import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import {
  BadgeIndianRupee,
  CalendarDays,
  Download,
  FileSpreadsheet,
  Filter,
  Landmark,
  ReceiptText,
  RefreshCcw,
  Search,
  ShieldCheck
} from 'lucide-react';
import Header from '../../components/Header';
import Sidebar from '../../components/Sidebar/sidebar';
import api from '../../utils/api';
import './CommissionLedger.css';

const today = () => new Date().toISOString().slice(0, 10);

const firstDayOfMonth = () => {
  const date = new Date();
  date.setDate(1);
  return date.toISOString().slice(0, 10);
};

const formatCurrency = (value) =>
  Number(value || 0).toLocaleString('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  });

const formatNumber = (value) => Number(value || 0).toLocaleString('en-IN');

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

const statusTone = {
  Matched: 'is-matched',
  Pending: 'is-pending',
  'Short Received': 'is-short',
  'Excess Received': 'is-excess'
};

const CommissionLedger = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [insurers, setInsurers] = useState([]);
  const [filters, setFilters] = useState({
    start: firstDayOfMonth(),
    end: today(),
    insurer: '',
    search: '',
    gstRate: '18',
    tdsRate: '10'
  });
  const [ledger, setLedger] = useState({
    summary: {},
    byInsurer: [],
    rows: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchInsurers = useCallback(async () => {
    try {
      const response = await api.get('/api/finance/insurers');
      setInsurers(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setInsurers([]);
    }
  }, []);

  const fetchLedger = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = Object.fromEntries(
        Object.entries(filters).filter(([, value]) => String(value || '').trim() !== '')
      );
      const response = await api.get('/api/finance/commission-ledger', { params });
      setLedger({
        summary: response.data?.summary || {},
        byInsurer: response.data?.byInsurer || [],
        rows: response.data?.rows || []
      });
    } catch (err) {
      setError(err?.response?.data?.error || 'Unable to load finance ledger.');
      setLedger({ summary: {}, byInsurer: [], rows: [] });
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchInsurers();
  }, [fetchInsurers]);

  useEffect(() => {
    fetchLedger();
  }, [fetchLedger]);

  const summaryCards = useMemo(() => ([
    {
      label: 'Booked Policies',
      value: formatNumber(ledger.summary.policyCount),
      note: 'Finance source rows',
      icon: FileSpreadsheet
    },
    {
      label: 'Commission',
      value: formatCurrency(ledger.summary.expectedCommission),
      note: 'Expected brokerage',
      icon: BadgeIndianRupee
    },
    {
      label: 'GST Output',
      value: formatCurrency(ledger.summary.commissionGst),
      note: `${filters.gstRate || 0}% on commission`,
      icon: ReceiptText
    },
    {
      label: 'TDS',
      value: formatCurrency(ledger.summary.expectedTds),
      note: `${filters.tdsRate || 0}% deduction`,
      icon: ShieldCheck
    },
    {
      label: 'Receivable',
      value: formatCurrency(ledger.summary.expectedReceivable),
      note: 'Commission + GST - TDS',
      icon: Landmark
    }
  ]), [filters.gstRate, filters.tdsRate, ledger.summary]);

  const exportRows = () => {
    const rows = ledger.rows.map((row) => ({
      'Policy Number': row.policyNumber,
      Client: row.clientName,
      'Client Type': row.clientType,
      Insurer: row.insurerName,
      Branch: row.branchName,
      Policy: row.policyName,
      'Start Date': formatDate(row.policyStartDate),
      'Gross Premium': row.grossPremium,
      'Net Premium': row.netPremium,
      'Brokerage %': row.brokeragePercentage,
      'Expected Commission': row.expectedCommission,
      'GST on Commission': row.commissionGst,
      'TDS': row.expectedTds,
      'Expected Receivable': row.expectedReceivable,
      Received: row.receivedAmount,
      Difference: row.difference,
      Status: row.status,
      Invoices: (row.invoiceNumbers || []).join(', ')
    }));

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Commission Ledger');
    XLSX.writeFile(workbook, `Commission_Ledger_${today()}.xlsx`);
  };

  const updateFilter = (key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
  };

  return (
    <div className="commission-container">
      <div className="sidebar">
        <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
      </div>
      <main className={`commission-content ${collapsed ? 'collapsed' : ''}`}>
        <div className="commission-sticky-header">
          <Header />
        </div>
        <div className="commission-scroll-area">
          <section className="commission-page">
            <div className="commission-heading">
              <div>
                <span className="commission-kicker">Finance</span>
                <h1>Commission Management</h1>
                <p>Track expected commission, GST output, TDS deduction, receivables, and insurer-wise reconciliation from booked policies.</p>
              </div>
              <div className="commission-actions">
                <button className="commission-btn is-light" type="button" onClick={fetchLedger}>
                  <RefreshCcw size={16} />
                  Refresh
                </button>
                <button className="commission-btn" type="button" onClick={exportRows} disabled={!ledger.rows.length}>
                  <Download size={16} />
                  Export
                </button>
              </div>
            </div>

            <div className="commission-summary">
              {summaryCards.map((card) => {
                const Icon = card.icon;
                return (
                  <article className="commission-summary-card" key={card.label}>
                    <Icon size={20} />
                    <div>
                      <span>{card.label}</span>
                      <strong>{card.value}</strong>
                      <small>{card.note}</small>
                    </div>
                  </article>
                );
              })}
            </div>

            <section className="commission-panel">
              <div className="commission-panel-title">
                <div>
                  <h2>Filters</h2>
                  <span>Use policy start date and insurer to narrow the finance ledger.</span>
                </div>
                <Filter size={18} />
              </div>

              <div className="commission-filters">
                <label>
                  <span>Start Date</span>
                  <div className="commission-input-icon">
                    <CalendarDays size={16} />
                    <input type="date" value={filters.start} onChange={(event) => updateFilter('start', event.target.value)} />
                  </div>
                </label>
                <label>
                  <span>End Date</span>
                  <div className="commission-input-icon">
                    <CalendarDays size={16} />
                    <input type="date" value={filters.end} onChange={(event) => updateFilter('end', event.target.value)} />
                  </div>
                </label>
                <label>
                  <span>Insurer</span>
                  <select value={filters.insurer} onChange={(event) => updateFilter('insurer', event.target.value)}>
                    <option value="">All Insurers</option>
                    {insurers.map((insurer) => (
                      <option value={insurer} key={insurer}>{insurer}</option>
                    ))}
                  </select>
                </label>
                <label>
                  <span>GST %</span>
                  <input type="number" min="0" step="0.01" value={filters.gstRate} onChange={(event) => updateFilter('gstRate', event.target.value)} />
                </label>
                <label>
                  <span>TDS %</span>
                  <input type="number" min="0" step="0.01" value={filters.tdsRate} onChange={(event) => updateFilter('tdsRate', event.target.value)} />
                </label>
                <label className="commission-search">
                  <span>Search</span>
                  <div className="commission-input-icon">
                    <Search size={16} />
                    <input value={filters.search} onChange={(event) => updateFilter('search', event.target.value)} placeholder="Policy, client, insurer, status" />
                  </div>
                </label>
              </div>
            </section>

            {error && <div className="commission-error">{error}</div>}

            <section className="commission-grid">
              <article className="commission-panel">
                <div className="commission-panel-title">
                  <div>
                    <h2>Insurer Summary</h2>
                    <span>Expected vs received by insurer.</span>
                  </div>
                </div>
                <div className="commission-table-wrap compact">
                  <table className="commission-table">
                    <thead>
                      <tr>
                        <th>Insurer</th>
                        <th>Policies</th>
                        <th>Receivable</th>
                        <th>Received</th>
                        <th>Difference</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ledger.byInsurer.map((row) => (
                        <tr key={row.insurerName}>
                          <td>{row.insurerName}</td>
                          <td>{formatNumber(row.policyCount)}</td>
                          <td>{formatCurrency(row.expectedReceivable)}</td>
                          <td>{formatCurrency(row.receivedAmount)}</td>
                          <td className={row.difference > 1 ? 'amount-negative' : 'amount-positive'}>{formatCurrency(row.difference)}</td>
                        </tr>
                      ))}
                      {!ledger.byInsurer.length && (
                        <tr>
                          <td colSpan="5" className="commission-empty">{loading ? 'Loading...' : 'No insurer data found.'}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </article>

              <article className="commission-panel">
                <div className="commission-panel-title">
                  <div>
                    <h2>Reconciliation Status</h2>
                    <span>Policy count by finance status.</span>
                  </div>
                </div>
                <div className="commission-status-list">
                  {['Pending', 'Matched', 'Short Received', 'Excess Received'].map((status) => (
                    <div className="commission-status-row" key={status}>
                      <span className={`commission-status ${statusTone[status] || ''}`}>{status}</span>
                      <strong>{formatNumber(ledger.summary.statusCounts?.[status] || 0)}</strong>
                    </div>
                  ))}
                </div>
              </article>
            </section>

            <section className="commission-panel">
              <div className="commission-panel-title">
                <div>
                  <h2>Booked Policy Ledger</h2>
                  <span>Expected commission, GST, TDS, receivable, receipt, and variance per policy.</span>
                </div>
                {loading && <span className="commission-loading">Loading</span>}
              </div>
              <div className="commission-table-wrap">
                <table className="commission-table">
                  <thead>
                    <tr>
                      <th>Policy</th>
                      <th>Client</th>
                      <th>Insurer</th>
                      <th>Start</th>
                      <th>Net Premium</th>
                      <th>Commission</th>
                      <th>GST</th>
                      <th>TDS</th>
                      <th>Receivable</th>
                      <th>Received</th>
                      <th>Diff.</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ledger.rows.map((row) => (
                      <tr key={row.policyId}>
                        <td>
                          <strong>{row.policyNumber || '-'}</strong>
                          <small>{row.policyName || '-'}</small>
                        </td>
                        <td>
                          <strong>{row.clientName || '-'}</strong>
                          <small>{row.clientType || '-'}</small>
                        </td>
                        <td>
                          <strong>{row.insurerName || '-'}</strong>
                          <small>{row.branchName || '-'}</small>
                        </td>
                        <td>{formatDate(row.policyStartDate)}</td>
                        <td>{formatCurrency(row.netPremium)}</td>
                        <td>{formatCurrency(row.expectedCommission)}</td>
                        <td>{formatCurrency(row.commissionGst)}</td>
                        <td>{formatCurrency(row.expectedTds)}</td>
                        <td>{formatCurrency(row.expectedReceivable)}</td>
                        <td>{formatCurrency(row.receivedAmount)}</td>
                        <td className={row.difference > 1 ? 'amount-negative' : 'amount-positive'}>{formatCurrency(row.difference)}</td>
                        <td><span className={`commission-status ${statusTone[row.status] || ''}`}>{row.status}</span></td>
                      </tr>
                    ))}
                    {!ledger.rows.length && (
                      <tr>
                        <td colSpan="12" className="commission-empty">{loading ? 'Loading ledger...' : 'No booked policies found for these filters.'}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          </section>
        </div>
      </main>
    </div>
  );
};

export default CommissionLedger;
